/**
 * Form data transformation utilities
 *
 * These helpers are used to clean and validate form data before submission,
 * particularly for dynamic field lists that may include placeholder rows.
 */

/**
 * Converts a string to an optional string (null if empty/whitespace-only)
 */
export const toOptionalString = (value?: string | null): string | null => {
	const trimmed = value?.trim();
	return trimmed && trimmed.length > 0 ? trimmed : null;
};

/**
 * Converts a Date or string to ISO string format (null if invalid)
 */
export const toISOString = (date?: Date | string | null): string | null => {
	if (!date) {
		return null;
	}

	if (date instanceof Date) {
		return date.toISOString();
	}

	// Handle string dates - try to parse and convert to ISO string
	const parsedDate = new Date(date);
	if (Number.isNaN(parsedDate.getTime())) {
		return null;
	}

	return parsedDate.toISOString();
};

/**
 * Filters out empty/uninitialized items from dynamic field list arrays.
 *
 * When working with field lists, users can add rows that may remain empty. These helper
 * functions ensure only initialized entries are sent to the backend.
 *
 * @param items - Array of form items to filter
 * @param keyFields - Field name(s) that must have values for the item to be considered valid.
 *                    Can be a single field name or an array of field names (any must be filled).
 * @param idField - Optional ID field name. Items with IDs are always kept (existing records).
 *
 * @example
 * // Filter contacts - keep if they have an ID or a contactValue
 * const validContacts = filterAutoExpandingItems(
 *   formValues.contacts,
 *   'contactValue',
 *   'customerContactId'
 * );
 *
 * @example
 * // Filter addresses - keep if they have an ID or any address field
 * const validAddresses = filterAutoExpandingItems(
 *   formValues.addresses,
 *   ['streetAddress', 'city', 'postalCode'],
 *   'customerAddressId'
 * );
 *
 * @example
 * // Filter beneficiaries - keep if they have both required fields
 * const validBeneficiaries = formValues.beneficiaries.filter(b => {
 *   return b.beneficiaryCustomerId?.trim() && b.effectiveFrom;
 * });
 */
export function filterAutoExpandingItems<T extends Record<string, any>>(
	items: T[],
	keyFields: string | string[],
	idField?: string
): T[] {
	const fields = Array.isArray(keyFields) ? keyFields : [keyFields];

	return items.filter(item => {
		// Always keep items with existing IDs (persisted records)
		if (idField && item[idField]) {
			return true;
		}

		// Check if any of the key fields has a value
		return fields.some(field => {
			const value = item[field];
			if (typeof value === 'string') {
				return toOptionalString(value) !== null;
			}
			return Boolean(value);
		});
	});
}

/**
 * Helper to check if an item should be included in submission.
 * Similar to filterAutoExpandingItems but returns boolean for a single item.
 *
 * @example
 * const shouldInclude = shouldIncludeItem(contact, 'contactValue', 'customerContactId');
 */
export function shouldIncludeItem<T extends Record<string, any>>(
	item: T,
	keyFields: string | string[],
	idField?: string
): boolean {
	// Always keep items with existing IDs (persisted records)
	if (idField && item[idField]) {
		return true;
	}

	const fields = Array.isArray(keyFields) ? keyFields : [keyFields];

	// Check if any of the key fields has a value
	return fields.some(field => {
		const value = item[field];
		if (typeof value === 'string') {
			return toOptionalString(value) !== null;
		}
		return Boolean(value);
	});
}
